import { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { Button, Form, Input, Space, Table, Tooltip, message, Modal, Divider, Dropdown, Typography, Select, Image, Upload, Radio, Switch, Alert, Spin, Popconfirm, Popover } from 'antd';
import copy from 'copy-to-clipboard';
import { conferenceOnceApi, fileApi, batchUpdateRoleApi, batchUpdateHiddenApi, deleteConferenceCache, updateGuestsShowTypeApi, getActivityIdentitiesApi } from '@services/Conference';
import {
    guestListApi,
    guestDelApi,
    guestAllSureApi,
    guestMessageChangeApi,
    exportGuestsApi,
    journeySettingDownloadApi,
    sync2registrationApi,
    batchDeleteGuestsApi,
    addInvitationOrgsApi,
    removeInvitationOrgsApi,
    resetInvitationOrgsApi,
    batchUpdateGuestLaborDemandApi,
    checkGuestLaborDemandChangeableApi,
    updateGuestRemarksApi,
} from '@services/Guest';
import { getOrganizations } from '@services/Experts';
import { organizationsSaveAndUpdateApi } from '@services/DC/Organization';
import { getConferenceListApi } from '@services/Agenda';
import { orgTypeList, identityTypes } from '@utils/constants';
import conferenceSlice, { conferenceActions } from '../store/conferenceSlice';
import { isEmpty, debounce } from 'lodash';
import { localStorageGet } from '@core/cookie';
import GuestJourneyForm from './components/GuestJourney';
import ItineraryScheduleSetting from './components/ItineraryScheduleSetting';
import GuestFilterPanel from './GuestFilterPanel';
import PageHeader from '@components/PageHeader/PageHeader';
import AddGuest from './components/AddGuest';
import GuestTask from './components/GuestTask';
import GuestMessage from './components/GuestMessage';
import WorkExperiences from '@/components/common/WorkExperiences';
import ExpertInfoCard from '@/components/common/ExpertInfoCard';
import GuestTaskList from '@/components/common/GuestTaskList';
import { getGuestRemarksFromRecord } from '@/components/common/guestRemarks';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';
import { useTableScrollY, dispatchTableScrollYRemeasure } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
import { workspaceConferenceGuestJourneysPath } from '@/layouts/conferenceWorkspacePaths';
import { convertDateTimeRangeToLocalText, prepareMedLevelGrade } from '@utils/utils';
import { fullPagerConfig } from '@utils/property';
import config from '@config';
import './GuestList.less';

const { Text, Link } = Typography;

const { setGuestFilter: setFilter, setGuestCursor: setCursor, setGuestCollapsed, setGuestJourneyFilter } = conferenceActions;

const guestLaborDemandList = [
    { value: 0, label: '待定', color: 'warning' },
    { value: 1, label: '需要', color: 'success' },
    { value: 2, label: '不需要', color: 'danger' },
];

const guestIdentityList = [
    { value: 1, label: '参会嘉宾' },
    { value: 2, label: '参会代表' },
    { value: 3, label: '企业人员' },
    { value: 4, label: '工作人员' },
    { value: 0, label: '待定' },
];

function isGuestJourneyReady(record) {
    const entity = record?.guestJourneysEntity;
    if (!entity) {
        return false;
    }

    if (entity.arrivalTransport) {
        try {
            const arrivalTransport = JSON.parse(entity.arrivalTransport);
            if (arrivalTransport?.transport_list?.length > 0) {
                return true;
            }
        } catch (error) {
            console.error('解析来程交通信息失败:', error);
        }
    }

    if (entity.returnTransport) {
        try {
            const returnTransport = JSON.parse(entity.returnTransport);
            if (returnTransport?.transport_list?.length > 0) {
                return true;
            }
        } catch (error) {
            console.error('解析返程交通信息失败:', error);
        }
    }

    if (entity.hotelInfo) {
        try {
            const hotelInfo = JSON.parse(entity.hotelInfo);
            if (hotelInfo?.hotel?.hotel_id) {
                return true;
            }
        } catch (error) {
            console.error('解析住宿信息失败:', error);
        }
    }

    return false;
}

function GuestListGuestRemarksCell({ remarks, onEdit }) {
    const textRef = useRef(null);
    const [overflow, setOverflow] = useState(false);
    const text = remarks ?? '';
    const isEmptyRemarks = !text.trim();

    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            setOverflow(false);
            return;
        }
        const check = () => setOverflow(el.scrollHeight > el.clientHeight + 1);
        check();
        const observer = new ResizeObserver(check);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text]);

    return (
        <div className="guest-remarks-cell">
            {isEmptyRemarks ? (
                <Text type="secondary">暂无</Text>
            ) : (
                <div className="guest-remarks-body">
                    <div ref={textRef} className="guest-remarks-text">
                        {text}
                    </div>
                    {overflow ? (
                        <Popover trigger="click" placement="topLeft" content={<div className="guest-remarks-full">{text}</div>}>
                            <span className="guest-remarks-view-all">查看完整备注</span>
                        </Popover>
                    ) : null}
                </div>
            )}
            <Button type="link" size="small" className="guest-remarks-edit" aria-label="编辑嘉宾备注" icon={<i className="bi bi-pencil" aria-hidden="true" />} onClick={onEdit} />
        </div>
    );
}

function renderGuestJourneyStatusCell(journeyReady, guestName, onOpen) {
    const trimmedName = typeof guestName === 'string' ? guestName.trim() : '';
    const label = journeyReady ? '已录入' : '未录入';

    return (
        <div className="guest-journey-status-cell">
            <div className="guest-journey-status-body">
                <Text type={journeyReady ? 'success' : 'secondary'}>{label}</Text>
            </div>
            <Button type="link" size="small" className="guest-journey-status-edit" aria-label={trimmedName ? `查看${trimmedName}嘉宾行程` : '查看嘉宾行程'} icon={<i className="bi bi-pencil" aria-hidden="true" />} disabled={!trimmedName} onClick={onOpen} />
        </div>
    );
}

export default function GuestList() {
    const params = useParams();
    const navigate = useNavigate();

    const conferenceId = params?.id || 0; // 会议项目 ID
    const [conference, setConference] = useState(null);

    const [laborFeeConfirmed, setLaborFeeConfirmed] = useState(false);
    const [laborDemandLoading, setLaborDemandLoading] = useState(false);

    const dispatch = useDispatch();
    const filter = useSelector(state => state.conference.guest.filter);
    const cursor = useSelector(state => state.conference.guest.cursor);
    const collapsed = useSelector(state => state.conference.guest.collapsed ?? true);

    const [form] = Form.useForm();

    const [selectedGuests, setSelectedGuests] = useState([]);
    const [selectedExpertsIds, setSelectedExpertsIds] = useState([]);
    const [selectedGuestIds, setSelectedGuestIds] = useState([]);

    const [visibleAddGuestsDrawer, setVisibleAddGuestsDrawer] = useState(false);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);

    const [dateOptions, setDateOptions] = useState([]);

    const [rowItem, setRowItem] = useState(undefined);

    const [showGuestForm] = Form.useForm();
    const [visibleGuestType, setVisibleGuestType] = useState(false);

    // 嘉宾行程表单 Modal
    const [visibleGuestJourney, setVisibleGuestJourney] = useState(false);
    const [currentGuestJourney, setCurrentGuestJourney] = useState(null); // 当前编辑的嘉宾行程

    const [visibleItineraryScheduleDrawer, setVisibleItineraryScheduleDrawer] = useState(false); // 行程与日程设置抽屉

    const [searchLoading, setSearchLoading] = useState(false);
    const [units, setUnits] = useState([]);
    const [authType, setAuthType] = useState('');
    const [unitSearchKeyword, setUnitSearchKeyword] = useState('');
    const curIndexRef = useRef(0); // 记录当前新增单位的索引

    // 新增单位
    const [createUnitForm] = Form.useForm();
    const [visibleCreateUnitModal, setVisibleCreateUnitModal] = useState(false);

    const [remarksModalOpen, setRemarksModalOpen] = useState(false);
    const [remarksSaving, setRemarksSaving] = useState(false);
    const [remarksEditing, setRemarksEditing] = useState(null);
    const [remarksForm] = Form.useForm();

    useEffect(() => {
        if (!remarksModalOpen) return;
        remarksForm.setFieldsValue({
            remarks: getGuestRemarksFromRecord(remarksEditing),
        });
    }, [remarksModalOpen, remarksEditing, remarksForm]);

    // 关闭嘉宾行程表单
    const closeGuestJourney = () => {
        setVisibleGuestJourney(false);
    };
    const [visibleSignUp, setVisibleSignUp] = useState(false); //同步报名

    const selectedRowsGuests = useRef(null);
    const [signUpLoading, setSignUpLoading] = useState(false);

    // 全屏状态
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const collapsedRemeasureMountedRef = useRef(false);

    useLayoutEffect(() => {
        if (!collapsedRemeasureMountedRef.current) {
            collapsedRemeasureMountedRef.current = true;
            return;
        }
        dispatchTableScrollYRemeasure();
    }, [collapsed]);

    // fixed-scroll 三件套：表格高度自适应 + 横向滚动守卫 + 纵向阴影
    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.main-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    // 会议列表
    const [conferenceHallTreeData, setConferenceHallTreeData] = useState([]);

    // 会议任务筛选状态
    const [hasTask, setHasTask] = useState(undefined);

    const getConferenceInfo = async conferenceId => {
        const res = await conferenceOnceApi({ id: conferenceId });
        if (res.status_code === 200) {
            setConference(res.data);
            setLaborFeeConfirmed(!!res.data?.laborFeeConfirmedAt);
        }
    };

    useEffect(() => {
        if (!!conferenceId) {
            getConferenceInfo(conferenceId);
        }
    }, []);

    const showAddGuestsDrawer = () => {
        setVisibleAddGuestsDrawer(true);
    };

    // 嘉宾任务管理 Drawer
    const [visibleGuestTasksDrawer, setVisibleGuestTasksDrawer] = useState(false);

    const [isJoureyModalOpen, setIsJoureyModalOpen] = useState(false);
    const [identityModalOpen, setIdentityModalOpen] = useState(false);
    const [fileList, setFileList] = useState([]);
    const currentJourney = useRef(null);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [currImage, setCurrImage] = useState('');
    const [taskModalOpen, setTaskModalOpen] = useState(false);
    const [taskContent, setTaskContent] = useState(null);

    const option = { headers: { 'Content-Type': 'multipart/form-data' } };

    const [journeyForm] = Form.useForm();
    const [identityForm] = Form.useForm();

    const [benefits, setBenefits] = useState([]); // vip嘉宾 - 对应权益

    // 新增/移除邀请方 Modal
    const [addInvitationForm] = Form.useForm();
    const [removeInvitationForm] = Form.useForm();
    const [visibleAddInvitation, setVisibleAddInvitation] = useState(false);
    const [visibleRemoveInvitation, setVisibleRemoveInvitation] = useState(false);
    const [addExclusive, setAddExclusive] = useState(false);

    const showGuestTasksDrawer = item => {
        dispatch(setCursor(item.id));
        setVisibleGuestTasksDrawer(true);
        setRowItem(item);
    };

    const closeGuestTasksDrawer = () => {
        setVisibleGuestTasksDrawer(false);
    };

    // 嘉宾展示信息 Drawer

    const [visibleGuestDisplayInfoDrawer, setVisibleGuestDisplayInfoDrawer] = useState(false);

    const showGuestDisplayInfoDrawer = item => {
        dispatch(setCursor(item.id));
        setVisibleGuestDisplayInfoDrawer(true);
        setRowItem(item);
    };

    const closeGuestDisplayInfoDrawer = () => {
        setVisibleGuestDisplayInfoDrawer(false);
        // setRowItem(undefined)
    };

    // 处理会议任务字段变化
    const handleHasTaskChange = value => {
        setHasTask(value);
        // 如果选择"无会议任务"，清空任务相关字段
        if (value === 0) {
            form.setFieldsValue({
                invitationAccepted: undefined,
                agendTime: undefined,
                conferenceHallId: undefined,
                taskBeginTime: undefined,
                taskEndTime: undefined,
            });
        }
    };

    const onFinish = values => {
        // 处理日期参数转换
        const newValues = { ...values };

        newValues.taskBeginTimeBegin = values.taskBeginTime && dayjs(values.taskBeginTime[0]).isValid() ? dayjs(values.taskBeginTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.taskBeginTimeEnd = values.taskBeginTime && dayjs(values.taskBeginTime[1]).isValid() ? dayjs(values.taskBeginTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.taskEndTimeBegin = values.taskEndTime && dayjs(values.taskEndTime[0]).isValid() ? dayjs(values.taskEndTime[0]).format('YYYY-MM-DD HH:mm') : undefined;
        newValues.taskEndTimeEnd = values.taskEndTime && dayjs(values.taskEndTime[1]).isValid() ? dayjs(values.taskEndTime[1]).format('YYYY-MM-DD HH:mm') : undefined;
        delete newValues.taskBeginTime;
        delete newValues.taskEndTime;

        const newFilter = {
            ...filter,
            ...newValues,
            page: 1,
        };
        // 兼容接口未处理 [] 的情况
        if (isEmpty(newFilter?.roles)) {
            delete newFilter.roles;
        }
        if (isEmpty(newFilter?.expertLevels)) {
            delete newFilter.expertLevels;
        }
        if (newFilter.hasRemarks !== 0 && newFilter.hasRemarks !== 1) {
            delete newFilter.hasRemarks;
        }
        const remarksKeyword = typeof newFilter.remarksKeyword === 'string' ? newFilter.remarksKeyword.trim() : '';
        if (newFilter.hasRemarks === 0 || !remarksKeyword) {
            delete newFilter.remarksKeyword;
        } else {
            newFilter.remarksKeyword = remarksKeyword;
        }
        dispatch(setFilter(newFilter));
    };

    const onReset = () => {
        form.resetFields();
        setHasTask(undefined);
        dispatch(setGuestCollapsed(true));
        dispatch(
            setFilter({
                ...conferenceSlice.getInitialState().guest.filter,
                pageSize: filter.pageSize,
            }),
        );
    };

    const handleToggleCollapsed = () => {
        dispatch(setGuestCollapsed(!collapsed));
    };

    const onDelReset = () => {
        dispatch(
            setFilter({
                ...filter,
            }),
        );
    };

    const onChangePage = (page, pageSize) => {
        dispatch(
            setFilter({
                ...filter,
                page,
                pageSize,
            }),
        );
    };

    const handleRefresh = () => {
        getExpertList();
    };

    // 排序点击处理
    const onClickSortDropdown = ({ key }) => {
        const newOrderMode = Number(key);
        // 如果点击的是当前排序方式，不重复请求
        if ((newOrderMode === 0 && !filter?.orderMode) || filter?.orderMode === newOrderMode) {
            return;
        }
        dispatch(
            setFilter({
                ...filter,
                orderMode: newOrderMode,
                page: 1,
            }),
        );
    };

    // 全屏切换
    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    const removeGuest = (memCard, expertId) => {
        const data = {
            conferenceId: params.id,
            memCard: memCard,
            expertId,
        };

        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: `移除该嘉宾，其相关会议任务将一并移除，确认移除？`,
            okText: '确认',
            centered: true,
            cancelText: '取消',
            onOk: () => {
                guestDelApi(data).then(res => {
                    if (res && res.status_code === 200) {
                        message.success('移除成功！');
                        onDelReset();
                    }
                });
            },
        });
    };

    // 删除行程单
    const delJourneyList = () => {
        journeyForm.setFieldValue('journeyList', '');
        setFileList([]);
    };

    // 确认行程单
    const handleJoureyOk = () => {
        journeyForm.validateFields().then(async values => {
            const rowInfo = currentJourney.current;
            const obj = {
                conferenceId: params.id,
                // memCard,
                journeyList: values?.journeyList,
                id: rowInfo?.guestsEntity?.id,
            };
            const res = await guestMessageChangeApi(obj);
            if (res.status_code === 200) {
                getExpertList();
                message.success('行程单保存成功');
            }
        });
        setIsJoureyModalOpen(false);
    };

    // 取消行程单
    const handleJoureyCancel = () => {
        setIsJoureyModalOpen(false);
    };

    // 批量修改嘉宾隐藏显示功能
    const batchUpdateHiddenAction = isHidden => {
        if (selectedGuestIds && selectedGuestIds.length > 0) {
            Modal.confirm({
                title: '提示',
                content: `是否确认将所选嘉宾${isHidden ? '隐藏' : '显示'}？`,
                cancelText: '取消',
                okText: '确认',
                closable: true,
                onOk: async () => {
                    const params = {
                        conferenceId,
                        hidden: isHidden ? 1 : 0,
                        ids: selectedGuestIds,
                    };
                    const res = await batchUpdateHiddenApi(params);
                    if (res.status_code === 200) {
                        getExpertList();
                    }
                },
            });
        } else {
            message.warning('尚未选择嘉宾');
        }
    };

    // 确认参会人员类别
    const handleIdentityOk = () => {
        identityForm.validateFields().then(async values => {
            const res = await batchUpdateRoleApi({ ids: selectedGuestIds, role: values.role });
            if (res.status_code === 200) {
                setIdentityModalOpen(false);
                message.success('更换参会人员类别成功');
                getExpertList();
                setSelectedExpertsIds([]);
                setSelectedGuestIds([]);
            }
        });
    };

    // 取消参会人员类别
    const handleIdentityCancel = () => {
        setIdentityModalOpen(false);
    };

    // 批量更换身份
    const bulkChangeIdentity = () => {
        if (selectedExpertsIds.length === 0) {
            return message.warning('尚未选择嘉宾');
        }
        setIdentityModalOpen(true);
    };

    // 行程单
    const handleJourneyList = row => {
        dispatch(setCursor(row.id));

        currentJourney.current = row;
        if (row?.guestsEntity?.journeyList) {
            setFileList([getFileUrl(row.guestsEntity.journeyList)]);
            journeyForm.setFieldValue('journeyList', row.guestsEntity.journeyList);
        }
        setIsJoureyModalOpen(true);
    };

    // 获取上传文件 - 前缀
    const getFileUrl = url => {
        if (url && url.includes('https')) {
            return url;
        }
        if (url && !url.includes('https')) {
            return config.OSS_PREFIX + url;
        }
        return null;
    };

    // 行程单
    const imageFile = {
        name: 'imageFile',
        customRequest: info => {
            const formData = new FormData();
            formData.append('file', info.file); //名字和后端接口名字对应
            formData.append('type', 14);
            fileApi(formData, option).then(res => {
                if (!res) {
                    message.error('上传文件失败！');
                    return false;
                }
                if (res.status_code === 200) {
                    setFileList([getFileUrl(res.data)]);
                    journeyForm.setFieldValue('journeyList', res.data);
                }
            });
        },
    };

    const checkout = (file, size) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        // 判断类型
        if (!isJpgOrPng) {
            message.error('只能上传 jpg/png 格式');
            return false;
        }
        // 判断大小
        const isLt2M = file.size / 1024 / 1024 < size;
        if (!isLt2M) {
            message.error('图像必须小于' + size + 'MB!');
            return false;
        }
        return isJpgOrPng && isLt2M;
    };

    const bulkConfirmTask = () => {
        if (selectedExpertsIds.length === 0) {
            message.warning('尚未选择嘉宾');
        } else {
            const obj = {
                conferenceId: params.id,
                expertIds: [...selectedExpertsIds],
                memCards: [...selectedGuests],
            };

            guestAllSureApi(obj).then(res => {
                if (res.status_code === 200) {
                    message.success('批量确认完成');
                    onResetGuest('noneChange');
                }
            });
        }
    };

    const onChangeHidden = (memCard, id, hidden) => {
        const obj = {
            conferenceId,
            memCard,
            hidden: Number(!hidden),
            id,
        };

        guestMessageChangeApi(obj).then(res => {
            if (res.status_code === 200) {
                getExpertList();
            }
        });
    };

    const openTaskModal = record => {
        dispatch(setCursor(record.id));
        setTaskContent(record);
        setTaskModalOpen(true);
    };

    const copyAgendaTasks = record => {
        let result = '';

        const linebreak = '\r\n';
        const linebreaks = '\r\n\r\n';

        const name = record?.memUsername;
        result += name + linebreaks;

        // const conferenceName = conference?.name;
        // result += conferenceName + linebreaks;

        const agendasGuests = record?.agendasGuestsList?.filter(g => g.invitationAccepted === 1) || [];

        // 内联处理议程任务数据
        const agendaDates = [];
        agendasGuests.forEach(t => {
            const date = dayjs(t.startTime).isValid() ? dayjs(t.startTime).format('M月D日') : '';
            const startTime = dayjs(t.startTime).isValid() ? dayjs(t.startTime).format('HH:mm') : '';
            const endTime = dayjs(t.endTime).isValid() ? dayjs(t.endTime).format('HH:mm') : '';

            const agenda = {
                agendaId: t.agendaId,
                agendaTitle: t.title,
                taskName: t.taskName,
                timeRange: `${startTime}～${endTime}`,
                invitationAccepted: t.invitationAccepted,
                refusalInfo: t.refusalInfo,
                startTime: t.startTime,
                endTime: t.endTime,
            };

            const agendaDate = agendaDates.find(d => d.date === date);
            if (agendaDate) {
                // 数据中会议日期已存在
                const hall = agendaDate.conferenceHalls.find(h => h.hallId === t.conferenceHallId);
                if (hall) {
                    // 数据中会议场次已存在
                    hall.agendas.push(agenda);
                } else {
                    // 新增会议场次
                    agendaDate.conferenceHalls.push({
                        hallId: t.conferenceHallId,
                        hallName: t.conferenceHallName,
                        place: t.place,
                        agendas: [agenda],
                    });
                }
            } else {
                agendaDates.push({
                    date,
                    conferenceHalls: [
                        {
                            hallId: t.conferenceHallId,
                            hallName: t.conferenceHallName,
                            place: t.place,
                            agendas: [agenda],
                        },
                    ],
                });
            }
        });

        agendaDates.forEach(agendaDate => {
            result += agendaDate.date + linebreak;

            agendaDate.conferenceHalls.forEach(hall => {
                result += `${hall.hallName}${hall.place ? ' / ' + hall.place : ''}` + linebreak;

                hall.agendas.forEach(a => {
                    result += `${a.timeRange} / ${a.taskName} / ${a.agendaTitle}` + linebreak;
                });
            });

            result += linebreak;
        });

        copy(result);
        message.success('复制成功');
    };

    const toggleAgendaTasks = guestAgendaInfo => {
        setData(
            data.map(item => {
                if (item.id === guestAgendaInfo.id) {
                    return { ...item, expanded: !item.expanded };
                } else {
                    return item;
                }
            }),
        );
    };

    // 渲染新的任务结构
    const renderGuestTaskList = record => {
        return <GuestTaskList agendasGuestsList={record.agendasGuestsList} expanded={record.expanded} onToggleExpansion={toggleAgendaTasks} record={record} />;
    };

    const openRemarksEditor = record => {
        setRemarksEditing(record);
        remarksForm.setFieldsValue({
            remarks: getGuestRemarksFromRecord(record),
        });
        setRemarksModalOpen(true);
    };

    const closeRemarksEditor = () => {
        setRemarksModalOpen(false);
        setRemarksEditing(null);
    };

    const handleSaveRemarks = async () => {
        const saveConferenceId = remarksEditing?.guestsEntity?.conferenceId ?? conferenceId;
        const expertId = remarksEditing?.id;
        if (!saveConferenceId || !expertId) {
            message.error('会议嘉宾不存在');
            return;
        }
        try {
            const values = await remarksForm.validateFields();
            const remarks = (values.remarks ?? '').trim();
            setRemarksSaving(true);
            const res = await updateGuestRemarksApi({ conferenceId: saveConferenceId, expertId, remarks });
            if (res?.status_code === 200) {
                message.success('嘉宾备注已保存');
                closeRemarksEditor();
                getExpertList();
            } else {
                message.error(res?.message || '保存嘉宾备注失败');
            }
        } catch (e) {
            if (e?.errorFields) {
                return;
            }
            console.error('保存嘉宾备注失败', e);
            message.error('保存嘉宾备注失败');
        } finally {
            setRemarksSaving(false);
        }
    };

    const columns = [
        {
            title: '参会人员类别',
            dataIndex: 'unit',
            width: 120,
            render: (_, record) => getGuestIdentity(record?.guestsEntity?.role),
        },
        {
            title: '基本信息',
            dataIndex: 'name',
            width: 180,
            render: (_, record) => <ExpertInfoCard expert={record} />,
        },
        {
            title: '单位任职',
            name: 'units',
            width: 200,
            render: (_, record) => <WorkExperiences workExperiences={record.ssoMemberWorkExperiencesEntities} emptyText="–" />,
        },
        {
            title: '会议任务',
            dataIndex: 'tasks',
            width: 320,
            render: (_, record) => renderGuestTaskList(record),
        },
        {
            title: '嘉宾备注',
            dataIndex: 'remarks',
            width: 220,
            render: (_, record) => <GuestListGuestRemarksCell remarks={getGuestRemarksFromRecord(record)} onEdit={() => openRemarksEditor(record)} />,
        },
        {
            title: '邀请方',
            name: 'organisations',
            width: 150,
            render: (_, record) => {
                const organisations = record?.guestsEntity?.organisations;
                const exclusiveId = record?.guestsEntity?.exclusiveInvitationOrgId;
                return organisations && !!organisations.length ? (
                    <span>
                        {organisations.map((item, index) => (
                            <span key={item.id || index}>
                                {item.name}
                                {exclusiveId && item.id === exclusiveId ? <span style={{ color: '#faad14' }}>(独家)</span> : null}
                                {index < organisations.length - 1 ? '、' : ''}
                            </span>
                        ))}
                    </span>
                ) : (
                    <Text type="secondary">&ndash;</Text>
                );
            },
        },
        {
            title: '一键显示/隐藏',
            dataIndex: 'hidden',
            width: 140,
            render: (_, record) =>
                !record?.guestsEntity?.hidden ? (
                    <Popconfirm title="确定隐藏？" okText="确定" cancelText="取消" onConfirm={() => onChangeHidden(record.memCard, record.guestsEntity.id, record.guestsEntity.hidden)}>
                        <Text type="success" className="hidden_pointer">
                            显示中
                        </Text>
                    </Popconfirm>
                ) : (
                    <Popconfirm title="确定显示？" okText="确定" cancelText="取消" onConfirm={() => onChangeHidden(record.memCard, record.guestsEntity.id, record.guestsEntity.hidden)}>
                        <Text type="warning" className="hidden_pointer">
                            隐藏中
                        </Text>
                    </Popconfirm>
                ),
        },
        {
            title: '嘉宾行程',
            dataIndex: 'hasJourneyInfo',
            width: 120,
            render: (_, record) => renderGuestJourneyStatusCell(isGuestJourneyReady(record), record?.memUsername, () => openGuestJourneyWithGuestName(record?.memUsername)),
        },
        {
            title: '同步至报名',
            dataIndex: 'syncedReg',
            width: 120,
            render: (_, record) => <>{record?.guestsEntity?.syncedReg === 1 ? <Text type="success">已同步</Text> : <Text type="danger">未同步</Text>}</>,
        },
        {
            title: (
                <span>
                    劳务需求
                    <Tooltip title={<span>劳务需求被标记“需要”的专家，才可在“专家劳务”模块进行劳务费申请。已有劳务费申请记录专家，不支持变更为“不需要”。无会议任务专家，不支持变更为“需要”。</span>}>
                        <i className="bi bi-question-circle-fill" style={{ marginLeft: 4, color: '#bfbfbf' }} />
                    </Tooltip>
                </span>
            ),
            dataIndex: 'laborFeeDemand',
            width: 120,
            render: (_, record) => {
                const item = guestLaborDemandList.find(m => m.value === record?.laborFeeDemand);
                return item ? <Text type={item.color}>{item.label}</Text> : <Text type="warning">待定</Text>;
            },
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 200,
            fixed: 'right',
            render: (_, record) => {
                const items = [
                    {
                        label: '移除会议嘉宾',
                        key: 'remove',
                        onClick: () => removeGuest(record.memCard, record.id),
                        danger: true,
                    },
                ];

                // 会议进入劳务阶段，禁用代嘉宾确认任务 !!conference?.laborFeeConfirmedAt
                return (
                    <span className="operation-btns">
                        {record?.agendasGuestsList?.filter(g => g.invitationAccepted === 1)?.length > 0 && (
                            <Button size="small" type="link" onClick={() => openTaskModal(record)}>
                                复制会议任务
                            </Button>
                        )}
                        <Button size="small" type="link" onClick={() => showGuestTasksDrawer(record)}>
                            代嘉宾确认任务
                        </Button>
                        <Button size="small" type="link" onClick={() => showGuestDisplayInfoDrawer(record)}>
                            嘉宾展示信息
                        </Button>
                        <Button size="small" type="link" onClick={() => downloadJourneySettings(record)}>
                            下载行程与日程提醒
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </span>
                );
            },
        },
    ];

    // 参会人员类别
    const getGuestIdentity = role => {
        if (role === 0) {
            return '待定';
        }
        if (role === 1) {
            return '参会嘉宾';
        }
        if (role === 2) {
            return '参会代表';
        }
        if (role === 3) {
            return '企业人员';
        }
        if (role === 4) {
            return '工作人员';
        }
    };

    // 嘉宾选择
    const guestSelection = {
        selectedRowKeys: selectedExpertsIds,
        preserveSelectedRowKeys: true,
        onChange: (selectedRowKeys, selectedRows) => {
            if (selectedRows.length > 0) {
                selectedRowsGuests.current = selectedRows;
                const memCard = selectedRows.map(item => {
                    return item.memCard;
                });
                // let expertsIds = selectedRows.map((item) => {
                //     return item.id;
                // });
                const guestIds = selectedRows.map(item => {
                    return item?.guestsEntity?.id;
                });

                setSelectedGuests([...memCard]);
                setSelectedExpertsIds([...selectedRowKeys]);
                setSelectedGuestIds([...guestIds]);
            } else {
                setSelectedGuests([]);
                setSelectedExpertsIds([]);
                setSelectedGuestIds([]);
                selectedRowsGuests.current = null;
            }
        },
    };

    // 获取嘉宾列表
    const getExpertList = async () => {
        const params = {
            ...filter,
            conferenceId,
        };

        try {
            setLoading(true);

            const res = await guestListApi(params);
            if (res.status_code === 200) {
                setLoading(false);

                setData(res.data.list);
                setTotal(res.data.totalCount);
            }

            setLoading(false);
        } catch (e) {
            setLoading(false);
        }
    };

    // 报名身份列表
    const getActivityIdentities = () => {
        getActivityIdentitiesApi({ activityId: conferenceId, activityType: 'conference' }).then(res => {
            if (res && res.status_code === 200 && res.data) {
                const benefits = res.data.find(item => item.isVip === 1)?.benefits;
                setBenefits(JSON.parse(benefits || '[]'));
            }
        });
    };

    useEffect(() => {
        const hasAdvancedFilter =
            filter.hasTask !== undefined
            || filter.invitationAccepted !== undefined
            || filter.agendTime !== undefined
            || filter.conferenceHallId !== undefined
            || filter.taskBeginTimeBegin !== undefined
            || filter.taskEndTimeBegin !== undefined
            || filter.syncedReg !== undefined
            || filter.hasInvitationOrg !== undefined
            || filter.laborFeeDemand !== undefined;
        if (hasAdvancedFilter && collapsed) {
            dispatch(setGuestCollapsed(false));
        }

        // 处理日期参数回显
        const newFilter = { ...filter };
        newFilter.taskBeginTime = filter.taskBeginTimeBegin && filter.taskBeginTimeEnd ? [dayjs(filter.taskBeginTimeBegin), dayjs(filter.taskBeginTimeEnd)] : undefined;
        newFilter.taskEndTime = filter.taskEndTimeBegin && filter.taskEndTimeEnd ? [dayjs(filter.taskEndTimeBegin), dayjs(filter.taskEndTimeEnd)] : undefined;

        form.setFieldsValue(newFilter);

        // 同步 hasTask 状态
        setHasTask(filter.hasTask);

        getExpertList();
    }, [filter]);

    const onResetGuest = type => {
        form.resetFields();

        dispatch(
            setFilter({
                ...filter,
                ...form.getFieldsValue(),
                page: 1,
            }),
        );
    };

    // 任务日期
    const getDataList = async () => {
        const res = await conferenceOnceApi({ id: conferenceId });
        if (res.status_code === 200 && res.data) {
            const dates = res.data.dates ? res.data.dates.split(',') : [];
            const datesArr = [];
            dates.forEach(item => {
                datesArr.push({ value: `${item} 00:00:00,${item} 11:59:59,AM`, label: `${dayjs(item).format('YYYY年MM月DD日')} 上午` });
                datesArr.push({ value: `${item} 12:00:00,${item} 23:59:59,PM`, label: `${dayjs(item).format('YYYY年MM月DD日')} 下午` });
            });
            setDateOptions(datesArr);
        }
    };

    useEffect(() => {
        getDataList();
        getActivityIdentities();
        getConferenceList();
    }, []);

    // 清空嘉宾缓存
    const clearConferenceGuestsCache = async () => {
        const data = {
            type: 'conference',
            key: `conference_${conferenceId}_user_guests`,
        };
        const res = await deleteConferenceCache(data);
        if (res?.status_code === 200) {
            message.success('更新会议官网嘉宾列表成功！');
        }
    };

    // 显示嘉宾类型
    const showGuestType = () => {
        setVisibleGuestType(true);
    };

    // 显示嘉宾类型
    const confirmShowGuestType = () => {
        showGuestForm.validateFields().then(async values => {
            const res = await updateGuestsShowTypeApi({ ...values, id: conferenceId });
            if (res.status_code === 200) {
                message.success('所选嘉宾显示成功');
                getExpertList();
                setVisibleGuestType(false);
            }
        });
    };

    // 清空嘉宾缓存
    const onClearConferenceGuestsCache = () => {
        if (conferenceId) {
            Modal.confirm({
                title: '更新官网嘉宾列表',
                icon: <i className="bi bi-exclamation-circle"></i>,
                width: 480,
                centered: true,
                content: (
                    <>
                        <p>为减轻性能压力，系统自动缓存数据，该功能将更新用户端数据，展现最新设定内容。</p>
                        <p>
                            <Typography.Text type="danger">会议进行期间谨慎更新！</Typography.Text>
                        </p>
                    </>
                ),
                onOk() {
                    clearConferenceGuestsCache();
                },
                cancelText: '取消，暂不更新',
                okText: '知晓，确认更新',
            });
        }
    };

    // 导出嘉宾列表
    const exportGuestsExcel = async () => {
        const res = await exportGuestsApi({ conferenceId });
        if (res.type === 'application/json') {
            return message.warning('暂无权限');
        } else {
            message.success('请稍后，正在导出');
            const content = res;
            const blob = new Blob([content], { type: 'application/octet-stream' });
            if ('download' in document.createElement('a')) {
                const fileNameSuffix = '_' + dayjs().format('YYYYMMDDHHmmss') + '.xlsx';
                const link = document.createElement('a');
                link.style.display = 'none';
                link.download = conference.name + '-嘉宾' + fileNameSuffix;
                link.href = URL.createObjectURL(blob);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                link.remove();
            } else {
                navigator.msSaveBlob(blob);
            }
        }
    };

    // 确认同步至报名
    const confirmSignUp = async () => {
        setSignUpLoading(true);
        const list = selectedRowsGuests.current.map(item => ({ expertId: item.id, memCard: item.memCard }));
        const res = await sync2registrationApi({
            conferenceId: conferenceId,
            guests: list,
        });
        setSignUpLoading(false);
        if (res && res.status_code === 200) {
            message.success('嘉宾同步报名签到成功');
            setVisibleSignUp(false);
        }
    };

    // 同步至报名
    const handleSignUp = () => {
        if (selectedRowsGuests.current && selectedRowsGuests.current.length > 0) {
            setVisibleSignUp(true);
        } else {
            return message.warning('尚未选择嘉宾');
        }
    };

    // 渲染二次确认中不可变更专家名单区域
    const renderBlockedNamesContent = (blockedNames, reasonText) => (
        <>
            <p>{reasonText}</p>
            <div
                style={{
                    maxHeight: 120,
                    overflow: 'auto',
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                    padding: '8px 12px',
                    backgroundColor: '#fffbe6',
                }}
            >
                <Text type="warning">无法变更需求专家：{blockedNames.join('、')}</Text>
            </div>
        </>
    );

    // 提交批量劳务需求变更
    const submitBatchUpdateLaborDemand = async (expertIds, laborFeeDemand) => {
        if (!expertIds.length) {
            message.warning('没有可变更需求的专家');
            return;
        }
        const res = await batchUpdateGuestLaborDemandApi({
            conferenceId,
            expertIds,
            laborFeeDemand,
        });
        if (res && res.status_code === 200) {
            message.success('专家劳务需求变更成功');
            setSelectedGuests([]);
            setSelectedExpertsIds([]);
            setSelectedGuestIds([]);
            selectedRowsGuests.current = null;
            getExpertList();
        }
    };

    // 调用接口检查哪些嘉宾可变更劳务需求，返回 { blockedNames, canChangeIds }
    // laborFeeDemand=1: withTaskExperts可变更，withoutTaskExperts不可变更
    // laborFeeDemand=2: withoutPaymentExperts可变更，withPaymentExperts不可变更
    const checkLaborDemandChangeable = async (changeableRows, targetValue) => {
        setLaborDemandLoading(true);
        try {
            const res = await checkGuestLaborDemandChangeableApi({
                conferenceId,
                expertIds: changeableRows.map(row => row.id).filter(Boolean),
                laborFeeDemand: targetValue,
            });
            if (res && res.status_code === 200) {
                const data = res.data || {};
                const isNeeded = targetValue === 1;
                const blockedExperts = isNeeded ? data.withoutTaskExperts || [] : data.withPaymentExperts || [];
                const changeableExperts = isNeeded ? data.withTaskExperts || [] : data.withoutPaymentExperts || [];
                const blockedNames = blockedExperts.map(e => e.memUsername).filter(Boolean);
                const canChangeIds = changeableExperts.map(e => e.id).filter(Boolean);
                return { blockedNames, canChangeIds };
            }
            // 接口异常时降级为全部可变更
            return { blockedNames: [], canChangeIds: changeableRows.map(row => row.id).filter(Boolean) };
        } finally {
            setLaborDemandLoading(false);
        }
    };

    // 标记需要劳务
    const handleMarkLaborDemandNeeded = async () => {
        const selectedRows = selectedRowsGuests.current || [];
        if (!selectedRows.length) {
            message.warning('请选择专家');
            return;
        }
        const targetValue = 1;
        const changeableRows = selectedRows.filter(row => row?.laborFeeDemand !== targetValue);
        if (!changeableRows.length) {
            message.warning('所选专家已全部是需要劳务状态');
            return;
        }

        // TODO: 不可变更原因文案待后端接口确认后替换
        const { blockedNames, canChangeIds } = await checkLaborDemandChangeable(changeableRows, targetValue);

        if (blockedNames.length > 0) {
            const allBlocked = canChangeIds.length === 0;
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
                content: renderBlockedNamesContent(blockedNames, allBlocked ? '以下专家因“无会议任务”或“全部任务未确认”情况，无法标记为“需要劳务”。' : '以下专家因“无会议任务”或“全部任务未确认”情况，无法标记为“需要劳务”。确认将其他专家的劳务需求统一标记为“需要劳务”？'),
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => {
                    submitBatchUpdateLaborDemand(canChangeIds, targetValue);
                },
            });
        } else {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
                content: '确认将所选专家的劳务需求统一变更为“需要”？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => {
                    submitBatchUpdateLaborDemand(canChangeIds, targetValue);
                },
            });
        }
    };

    // 标记无需劳务
    const handleMarkLaborDemandNotNeeded = async () => {
        const selectedRows = selectedRowsGuests.current || [];
        if (!selectedRows.length) {
            message.warning('请选择专家');
            return;
        }
        const targetValue = 2;
        const changeableRows = selectedRows.filter(row => row?.laborFeeDemand !== targetValue);
        if (!changeableRows.length) {
            message.warning('所选专家已全部是不需要劳务状态');
            return;
        }

        const { blockedNames, canChangeIds } = await checkLaborDemandChangeable(changeableRows, targetValue);

        if (blockedNames.length > 0) {
            const allBlocked = canChangeIds.length === 0;
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
                content: renderBlockedNamesContent(blockedNames, allBlocked ? '以下专家因有劳务费申请记录，无法标记为“无需劳务。' : '以下专家因有劳务费申请记录，无法标记为“无需劳务”。确认将其他专家的劳务需求统一标记为“无需劳务”？'),
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => {
                    submitBatchUpdateLaborDemand(canChangeIds, targetValue);
                },
            });
        } else {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle" aria-hidden="true" />,
                content: '确认将所选专家的劳务需求统一变更为“不需要”？',
                okText: '确认',
                cancelText: '取消',
                centered: true,
                onOk: () => {
                    submitBatchUpdateLaborDemand(canChangeIds, targetValue);
                },
            });
        }
    };

    // 嘉宾行程管理
    const guestJourneyManage = () => {
        navigate(workspaceConferenceGuestJourneysPath(conferenceId));
    };

    const openGuestJourneyWithGuestName = guestName => {
        const trimmedName = typeof guestName === 'string' ? guestName.trim() : '';
        if (!trimmedName) {
            return;
        }
        const { filter: initialFilter } = conferenceSlice.getInitialState().guestJourney;
        dispatch(
            setGuestJourneyFilter({
                ...initialFilter,
                name: trimmedName,
                page: 1,
            }),
        );
        navigate(workspaceConferenceGuestJourneysPath(conferenceId), { state: { guestName: trimmedName } });
    };

    // 根据关键字查询机构列表
    const requestOrgList = useCallback(
        async keyword => {
            setSearchLoading(true);

            try {
                const res = await getOrganizations({ keyword });
                if (!isEmpty(res.data)) {
                    setUnits(res.data);
                } else {
                    setUnits([]);
                }

                setSearchLoading(false);
            } catch (e) {
                setSearchLoading(false);
            }
        },
        [authType],
    );

    // 避免频繁请求
    const debouncedRequestOrgList = useCallback(
        debounce(keyword => {
            if (!!keyword) {
                requestOrgList(keyword);
            } else {
                setUnits([]);
            }
        }, 500),
        [requestOrgList],
    );

    useEffect(() => {
        debouncedRequestOrgList(unitSearchKeyword);

        // 组件卸载时取消未执行的防抖函数
        return () => debouncedRequestOrgList.cancel();
    }, [unitSearchKeyword]);

    const onShowCreateUnitModal = index => {
        curIndexRef.current = index;
        setVisibleCreateUnitModal(true);
        createUnitForm.setFieldValue('unitName', unitSearchKeyword);
    };

    // 机构名称搜索
    const onChangeUnitSearchKeyword = v => setUnitSearchKeyword(v);

    // 机构选择
    const onChangeUnit = id => {
        if (!!id) {
            const orgType = units?.find(unit => unit.id === id).orgType;

            if (!!orgType) {
                const identity = identityTypes.find(type => type.orgType === orgType)?.code;
                setAuthType(identity);
            } else {
                setAuthType('');
            }
        }
    };

    // 新增单位名称（机构）
    const onCreateUnit = () => {
        createUnitForm.validateFields().then(async values => {
            const res = await organizationsSaveAndUpdateApi({
                name: values.unitName,
                orgType: values.orgType,
            });
            if (!isEmpty(res.data)) {
                setUnits([res.data]);
                // 将新创建的机构ID写入“新增邀请方”表单
                const invitationOrgIds = addInvitationForm.getFieldValue('invitationOrgIds') || [];
                invitationOrgIds[curIndexRef.current] = res.data.id;
                addInvitationForm.setFieldsValue({ invitationOrgIds });

                const identity = identityTypes.find(type => type.orgType === values.orgType)?.code;
                setAuthType(identity);

                message.success('机构创建成功');

                createUnitForm.resetFields();
                setVisibleCreateUnitModal(false);
            } else {
                // message.error('机构创建失败，请稍后重试');
            }
        });
    };

    // 取消新增单位名称（机构）
    const onCancelCreateUnit = () => {
        createUnitForm.resetFields();
        setVisibleCreateUnitModal(false);
    };

    // 准备单位所在地信息
    const prepareLocation = unit => {
        let location = '';

        if (unit?.province) {
            location += unit.province;

            // 如果有市，且不是省直辖县级行政区划（北京、上海、天津、重庆），则添加市
            // 避免出现"北京市"为"北京"的情况
            const municipalityRegx = /^(北京|上海|天津|重庆)/;
            if (!!unit?.city && !municipalityRegx.test(unit.province)) {
                location += unit.city;
            }

            // 如果有区县，则添加区县（包括直辖市的区县）
            if (!!unit?.district) {
                location += unit.district;
            }
        }
        return location;
    };

    // 准备机构别称
    const prepareOrgAliase = unit => {
        if (!unit?.aliases) return '';

        try {
            // 解析JSON格式的别称数据
            const aliases = JSON.parse(unit.aliases);
            if (!Array.isArray(aliases) || aliases.length === 0) return '';

            // 分离当前别称和曾用名
            const currentAliases = aliases.filter(alias => !alias.former);
            const formerAliases = aliases.filter(alias => alias.former);

            let aliasText = '';

            // 添加当前别称
            if (currentAliases.length > 0) {
                const currentNames = currentAliases.map(alias => alias.name).join('、');
                aliasText += currentNames;
            }

            // 添加曾用名
            if (formerAliases.length > 0) {
                const formerNames = formerAliases.map(alias => `${alias.name}(曾用名)`).join('、');
                if (!!aliasText) aliasText += '、';
                aliasText += formerNames;
            }

            return aliasText;
        } catch (error) {
            console.error('解析机构别称失败:', error);
            return '';
        }
    };

    // 行程与日程提醒设置
    const itineraryScheduleSetting = () => {
        setVisibleItineraryScheduleDrawer(true);
    };

    // 下载行程与日程提醒
    const downloadJourneySettings = async record => {
        const params = {
            conferenceId: conferenceId,
            expertId: record.id,
        };
        const res = await journeySettingDownloadApi(params);
        const blob = new Blob([res], { type: 'application/octet-stream' });

        const post = record?.ssoMemberWorkExperiencesEntities ? record.ssoMemberWorkExperiencesEntities[0]?.unitPost : '';
        const name = record?.guestsEntity?.conferenceName;
        if ('download' in document.createElement('a')) {
            const link = document.createElement('a');
            link.style.display = 'none';
            link.download = `${record?.memUsername}${post}_行程与日程提醒_${name}.docx`;
            link.href = URL.createObjectURL(blob);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            link.remove();
        } else {
            navigator.msSaveBlob(blob);
        }
    };

    // 会议论坛列表 - 筛选
    const prepareConferenceHallTreeData = data => {
        const treeData = [];

        if (Object.keys(data) && Object.keys(data).length) {
            Object.keys(data).forEach(agendaDate => {
                const dateNode = {
                    title: agendaDate,
                    value: agendaDate,
                    selectable: false,
                    hallNum: 0,
                    children: [],
                };

                const confHalls = data[agendaDate];
                confHalls
                    && confHalls.length
                    && confHalls.forEach(confHall => {
                        const hallNode = {
                            title: `${confHall.name} [YHYC#${confHall.id}]`,
                            value: confHall.id,
                            children: [],
                        };

                        const confSubHalls = confHall.secondHallsList || [];
                        if (confSubHalls && confSubHalls.length) {
                            hallNode.selectable = false;

                            confSubHalls.forEach(subHall => {
                                dateNode.hallNum += 1;

                                hallNode.children.push({
                                    title: `${subHall.name} [YHYC#${subHall.id}]`,
                                    value: subHall.id,
                                });
                            });
                        } else {
                            dateNode.hallNum += 1;
                        }

                        dateNode.children.push(hallNode);
                    });

                treeData.push(dateNode);
            });
        }
        return treeData;
    };

    // 获取会议列表
    const getConferenceList = async () => {
        const res = await getConferenceListApi({ conferenceId });
        if (res.status_code === 200 && res.data) {
            const agendaData = res.data;
            setConferenceHallTreeData(prepareConferenceHallTreeData(agendaData));
        }
    };

    // 批量移除嘉宾
    const handleBatchRemoveGuests = () => {
        if (!selectedRowsGuests.current || selectedRowsGuests.current.length === 0) {
            message.warning('尚未选择嘉宾');
            return;
        }

        // 检查所有选中嘉宾是否有未拒绝状态的日程任务
        const hasUnrejectedTasks = selectedRowsGuests.current.some(guest => {
            const agendasGuestsList = guest?.agendasGuestsList || [];
            // 检查是否有 invitationAccepted 为 0（待确认）、1（接受）、3（待定）的任务
            return agendasGuestsList.some(task => {
                const status = task.invitationAccepted;
                return status === 0 || status === 1 || status === 3;
            });
        });

        if (hasUnrejectedTasks) {
            Modal.error({
                title: '提示',
                content: '批量移除的嘉宾中有待确认/接受/待定状态的会议任务，无法移除',
                centered: true,
            });
            return;
        }

        // 所有嘉宾都没有未拒绝的任务，可以执行删除
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            content: `移除所选嘉宾，其相关会议任务将一并移除，确认移除？`,
            okText: '确认',
            centered: true,
            cancelText: '取消',
            onOk: async () => {
                try {
                    const expertIds = selectedRowsGuests.current.map(item => item.id);
                    const res = await batchDeleteGuestsApi({
                        conferenceId,
                        expertIds,
                    });

                    if (res && res.status_code === 200) {
                        message.success(res.message || '移除成功！');
                        // 重置选中状态
                        setSelectedGuests([]);
                        setSelectedExpertsIds([]);
                        setSelectedGuestIds([]);
                        selectedRowsGuests.current = null;
                        // 刷新列表
                        getExpertList();
                    } else {
                        // message.error(res.message || '移除失败');
                        // 如果有失败详情，显示详细信息
                        if (Array.isArray(res.data) && res.data.length > 0) {
                            console.error('删除失败详情:', res.data);
                        }
                    }
                } catch (error) {
                    console.error('批量移除嘉宾失败:', error);
                    message.error(error.response?.data?.message || '移除操作失败');
                }
            },
        });
    };

    // 批量更换身份 - 更多展开
    const handleChangeItemClick = e => {
        const key = e.key;
        if (key === '1') {
            bulkConfirmTask();
        }
        if (key === '2') {
            batchUpdateHiddenAction(0);
        }
        if (key === '3') {
            batchUpdateHiddenAction(1);
        }
        if (key === '4') {
            handleBatchRemoveGuests();
        }
        if (key === '6') {
            openAddInvitationModal();
        }
        if (key === '7') {
            openRemoveInvitationModal();
        }
    };

    const changeItemItems = [
        {
            label: '批量确认任务',
            key: '1',
        },
        {
            label: '显示嘉宾',
            key: '2',
            disabled: selectedGuestIds.length <= 0,
        },
        {
            label: '隐藏嘉宾',
            key: '3',
            disabled: selectedGuestIds.length <= 0,
        },
        {
            label: '新增邀请方',
            key: '6',
            disabled: selectedGuestIds.length <= 0,
        },
        {
            label: '移除邀请方',
            key: '7',
            disabled: selectedGuestIds.length <= 0,
        },
        {
            label: '移除嘉宾',
            key: '4',
            disabled: selectedGuestIds.length <= 0,
            danger: true,
        },
    ];

    const menuPropsChangeItem = {
        items: changeItemItems,
        onClick: handleChangeItemClick,
    };

    // 打开/关闭/提交 - 新增邀请方
    const openAddInvitationModal = () => {
        if (!selectedRowsGuests.current || selectedRowsGuests.current.length === 0) {
            return message.warning('尚未选择嘉宾');
        }
        setAddExclusive(false);
        addInvitationForm.resetFields();
        setVisibleAddInvitation(true);
    };

    const closeAddInvitationModal = () => {
        setVisibleAddInvitation(false);
    };

    const confirmAddInvitation = () => {
        addInvitationForm.validateFields().then(async values => {
            // 非独家模式下，校验所选嘉宾的独家邀请方是否包含在 Modal 已选新增邀请方内
            if (!values.exclusive) {
                const guests = selectedRowsGuests.current || [];
                const exclusiveIds = Array.from(new Set(guests.map(g => g?.guestsEntity?.exclusiveInvitationOrgId).filter(id => !!id)));
                if (exclusiveIds.length > 0) {
                    const normalizedInviterIds = (values.invitationOrgIds || [])
                        .filter(v => v !== undefined && v !== null)
                        .map(v => Number(v))
                        .filter(v => !Number.isNaN(v));
                    const allMatched = exclusiveIds.every(id => normalizedInviterIds.includes(Number(id)));
                    if (!allMatched) {
                        message.warning('所选嘉宾中有独家邀请方，请移除后重试');
                        return;
                    }
                }
            }
            const guestIds = (selectedRowsGuests.current || []).map(item => Number(item.id)).filter(v => !Number.isNaN(v));
            const payload = {
                conferenceId: Number(conferenceId),
                guestIds,
            };
            if (values.exclusive) {
                const exclusiveId = Number(values.exclusiveInvitationOrgId || 0);
                payload.exclusiveInvitationOrgId = exclusiveId;
                payload.invitationOrgIds = Number.isNaN(exclusiveId) || exclusiveId === 0 ? [] : [exclusiveId];
            } else {
                const invitationOrgIds = (values.invitationOrgIds || []).map(id => Number(id)).filter(v => !Number.isNaN(v));
                payload.invitationOrgIds = invitationOrgIds;
                payload.exclusiveInvitationOrgId = 0;
            }

            const res = await addInvitationOrgsApi(payload);
            if (res && res.status_code === 200) {
                message.success('新增邀请方成功');
                setVisibleAddInvitation(false);
                addInvitationForm.resetFields();
                getExpertList();
            }
        });
    };

    // 打开/关闭/提交 - 移除邀请方
    const openRemoveInvitationModal = () => {
        if (!selectedRowsGuests.current || selectedRowsGuests.current.length === 0) {
            return message.warning('尚未选择嘉宾');
        }
        removeInvitationForm.resetFields();
        setVisibleRemoveInvitation(true);
    };

    const closeRemoveInvitationModal = () => {
        setVisibleRemoveInvitation(false);
    };

    const confirmRemoveInvitation = () => {
        removeInvitationForm.validateFields().then(async values => {
            const guestIds = (selectedRowsGuests.current || []).map(item => Number(item.id)).filter(v => !Number.isNaN(v));
            const invitationOrgIds = (values.invitationOrgIds || []).map(id => Number(id)).filter(v => !Number.isNaN(v));
            const payload = {
                conferenceId: Number(conferenceId),
                guestIds,
                invitationOrgIds,
            };
            const res = await removeInvitationOrgsApi(payload);
            if (res && res.status_code === 200) {
                message.success('移除邀请方成功');
                setVisibleRemoveInvitation(false);
                removeInvitationForm.resetFields();
                getExpertList();
            }
        });
    };

    // 重置邀请方（清空）
    const resetInvitationOrgs = () => {
        const guestIds = (selectedRowsGuests.current || []).map(item => Number(item.id)).filter(v => !Number.isNaN(v));
        if (!guestIds.length) {
            return message.warning('尚未选择嘉宾');
        }
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            width: 480,
            centered: true,
            content: <>重置将会清空所选专家的邀请方，确认重置？</>,
            onOk: async () => {
                const res = await resetInvitationOrgsApi({ conferenceId: Number(conferenceId), guestIds });
                if (res && res.status_code === 200) {
                    message.success('重置成功');
                    setVisibleRemoveInvitation(false);
                    removeInvitationForm.resetFields();
                    getExpertList();
                }
            },
        });
    };

    // 是否拥有嘉宾行程管理权限
    const canConfirmGuestJourney = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '嘉宾行程管理');
        }
        return false;
    }, []);

    // 是否拥有嘉宾行程导出权限
    const canExportGuestInfo = useMemo(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList && roleList.length) {
            return roleList.some(item => item.name === '导出嘉宾信息');
        }
        return false;
    }, []);

    useEffect(() => {
        document.title = '嘉宾管理-医会议';
    }, []);

    return (
        <>
            <div className="page-container guest-list-page">
                <PageHeader title="会议嘉宾" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '会议嘉宾']} {...filterToggleProps} />

                <FilterPanelSlot>
                    <GuestFilterPanel filter={filter} conferenceHallTreeData={conferenceHallTreeData} dateOptions={dateOptions} collapsed={collapsed} onFinish={onFinish} onReset={onReset} onToggleCollapsed={handleToggleCollapsed} />
                </FilterPanelSlot>

                <div ref={tableRegionRef} className="guest-list-table-region">
                    <div className="main-toolbar guest-list-toolbar boss-admin ant-flex ant-flex-wrap-wrap ant-flex-align-center ant-flex-justify-space-between ant-flex-gap-middle">
                        <div className="main-tools">
                            <Space>
                                <Button type="primary" onClick={() => showAddGuestsDrawer()}>
                                    添加嘉宾
                                </Button>

                                <Space.Compact className="guest-dropdown-button">
                                    <Button type="default" onClick={bulkChangeIdentity} style={{ width: 140 }}>
                                        更换参会人员类别
                                    </Button>
                                    <Dropdown menu={menuPropsChangeItem} trigger={['click']}>
                                        <Button type="default" icon={<i className="bi bi-chevron-down"></i>} />
                                    </Dropdown>
                                </Space.Compact>

                                <Button onClick={handleSignUp}>同步至报名</Button>

                                {/* TODO: 待确认：会议项目级劳务需求设置（labelPayment）是否禁用/隐藏以下两个按钮 */}
                                <Button onClick={handleMarkLaborDemandNeeded} loading={laborDemandLoading}>
                                    标记需要劳务
                                </Button>

                                <Button onClick={handleMarkLaborDemandNotNeeded} loading={laborDemandLoading}>
                                    标记无需劳务
                                </Button>
                            </Space>
                        </div>

                        <div className="sub-tools">
                            <Space>
                                <Button onClick={itineraryScheduleSetting}>行程与日程提醒设置</Button>

                                <Button onClick={showGuestType}>显示嘉宾类型</Button>

                                <Button danger onClick={() => onClearConferenceGuestsCache()}>
                                    更新官网嘉宾
                                </Button>

                                {canExportGuestInfo && (
                                    <Tooltip title="导出嘉宾">
                                        <Button
                                            type="default"
                                            icon={
                                                <span className="anticon">
                                                    <i className="bi bi-download"></i>
                                                </span>
                                            }
                                            onClick={exportGuestsExcel}
                                        />
                                    </Tooltip>
                                )}

                                <Dropdown
                                    menu={{
                                        items: [
                                            { key: 0, label: <>{!filter?.orderMode ? <Link>按添加时间倒序（最新在前，默认）</Link> : '按添加时间倒序（最新在前，默认）'}</> },
                                            { key: 1, label: <>{filter?.orderMode === 1 ? <Link>按嘉宾姓名拼音字母排序（A→Z）</Link> : '按嘉宾姓名拼音字母排序（A→Z）'}</> },
                                            { key: 2, label: <>{filter?.orderMode === 2 ? <Link>按日程最早开始时间正序（最早在前）</Link> : '按日程最早开始时间正序（最早在前）'}</> },
                                            { key: 3, label: <>{filter?.orderMode === 3 ? <Link>按日程最晚结束时间倒序（最晚在前）</Link> : '按日程最晚结束时间倒序（最晚在前）'}</> },
                                        ],
                                        onClick: onClickSortDropdown,
                                    }}
                                >
                                    <Tooltip title="排序方式">
                                        <Button
                                            type="default"
                                            className={filter?.orderMode ? 'sort-button-active' : ''}
                                            icon={
                                                <span className="anticon">
                                                    <i className="bi bi-sort-down"></i>
                                                </span>
                                            }
                                        />
                                    </Tooltip>
                                </Dropdown>

                                <Tooltip title="刷新列表">
                                    <Button
                                        type="default"
                                        icon={
                                            <span className="anticon">
                                                <i className="bi bi-arrow-clockwise"></i>
                                            </span>
                                        }
                                        onClick={handleRefresh}
                                    />
                                </Tooltip>

                                <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                    <Button
                                        type="default"
                                        icon={
                                            <span className="anticon">
                                                <i className={`bi ${isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                                            </span>
                                        }
                                        onClick={handleToggleFullscreen}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                    </div>

                    <div className="guest-list-table-wrap">
                        <Table
                            className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                            rowKey="id"
                            rowSelection={{ type: 'checkbox', ...guestSelection }}
                            rowClassName={row => (row.id === cursor ? 'store-cursor' : '')}
                            columns={columns}
                            dataSource={data}
                            loading={loading}
                            pagination={{
                                ...fullPagerConfig,
                                showSizeChanger: { placement: 'topRight' },
                                total,
                                showTotal: () => `共${total}条`,
                                current: filter.page,
                                pageSize: filter.pageSize,
                                onChange: onChangePage,
                            }}
                            scroll={{
                                x: 1600,
                                ...(tableScrollY != null ? { y: tableScrollY } : {}),
                            }}
                        />
                    </div>
                </div>
            </div>

            <Modal title="嘉宾行程单" open={isJoureyModalOpen} onOk={handleJoureyOk} onCancel={handleJoureyCancel} className="journeyListModal">
                <Form
                    name="journeyForm"
                    form={journeyForm}
                    // size="large"
                    autoComplete="off"
                >
                    <Form.Item name="journeyList" label="嘉宾行程单" extra="支持 JPG/PNG 格式，大小不超过5MB">
                        {fileList.length > 0 && (
                            <div className="imageUpload">
                                <Image width={200} src={fileList[0]} className="share-image-area" alt="嘉宾行程单预览" />
                                <i className="bi bi-x-circle"></i>
                            </div>
                        )}
                        <Upload beforeUpload={file => checkout(file, 5)} fileList={[]} {...imageFile} maxCount={1}>
                            {fileList.length === 0 && <Button icon={<i className="bi bi-upload"></i>}>上传嘉宾行程</Button>}
                        </Upload>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="新增邀请方"
                width={600}
                open={visibleAddInvitation}
                onCancel={closeAddInvitationModal}
                centered
                footer={[
                    <Button key="cancel" onClick={closeAddInvitationModal}>
                        取消
                    </Button>,
                    <Button key="ok" type="primary" onClick={confirmAddInvitation}>
                        确认新增
                    </Button>,
                ]}
            >
                <Form name="addInvitationForm" form={addInvitationForm} layout="vertical" initialValues={{ exclusive: false, invitationOrgIds: [undefined] }}>
                    <Alert title={<>{!!selectedRowsGuests.current?.length && <>已选专家：{selectedRowsGuests.current.map(item => item.memUsername).join('、')}</>}</>} type="warning" style={{ marginBottom: 16 }} />

                    <Form.Item label="独家邀请" name="exclusive" valuePropName="checked">
                        <Switch checkedChildren="是" unCheckedChildren="否" onChange={checked => setAddExclusive(checked)} />
                    </Form.Item>

                    {!addExclusive && (
                        <Form.Item label="邀请方" name="invitationOrgIds" className="required">
                            <Form.List name="invitationOrgIds">
                                {(fields, { add, remove }) => (
                                    <>
                                        <Button
                                            type="primary"
                                            size="small"
                                            ghost
                                            onClick={() => {
                                                const guests = selectedRowsGuests.current || [];
                                                const exclusiveIds = Array.from(new Set(guests.map(g => g?.guestsEntity?.exclusiveInvitationOrgId).filter(id => !!id)));
                                                if (exclusiveIds.length > 0) {
                                                    const inviterIds = addInvitationForm.getFieldValue('invitationOrgIds') || [];
                                                    const normalizedInviterIds = inviterIds
                                                        .filter(v => v !== undefined && v !== null)
                                                        .map(v => Number(v))
                                                        .filter(v => !Number.isNaN(v));
                                                    const allMatched = exclusiveIds.every(id => normalizedInviterIds.includes(Number(id)));
                                                    if (!allMatched) {
                                                        message.warning('所选嘉宾中有独家邀请方，请移除后重试');
                                                        return;
                                                    }
                                                }
                                                add();
                                            }}
                                            disabled={fields.length >= 10}
                                            style={{ marginBottom: 10 }}
                                        >
                                            <i className="bi bi-plus-circle"></i>
                                            新增邀请方
                                        </Button>

                                        {fields.map((field, index) => (
                                            <div key={field.key} className="invitation-item" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                                <Form.Item {...field} name={[field.name]} rules={[{ required: true, message: '请输入单位名称' }]} style={{ flex: 1, marginBottom: 0 }}>
                                                    <Select
                                                        optionLabelProp="label"
                                                        placeholder="请输入单位名称关键字检索"
                                                        suffixIcon={null}
                                                        allowClear
                                                        defaultActiveFirstOption={false}
                                                        onChange={onChangeUnit}
                                                        showSearch={{
                                                            filterOption: false,
                                                            onSearch: onChangeUnitSearchKeyword,
                                                        }}
                                                        styles={{ popup: { root: { width: '500px !important', minWidth: 500 } } }}
                                                        getPopupContainer={triggerNode => triggerNode.parentNode}
                                                        popupRender={menu => (
                                                            <>
                                                                {menu}
                                                                {!!units && units.some(unit => !unit.orgType) && <div style={{ padding: '8px 12px', backgroundColor: '#fff7e6', color: '#fa8c16' }}>未分类的机构不可选择，请在数据中心-机构管理中完善后再选择</div>}
                                                            </>
                                                        )}
                                                        notFoundContent={
                                                            searchLoading ? (
                                                                <div style={{ display: 'flex', justifyContent: 'center', padding: '5px 0' }}>
                                                                    <Spin />
                                                                </div>
                                                            ) : (
                                                                unitSearchKeyword && (
                                                                    <div className="emptyUnitSelectBox">
                                                                        <span>
                                                                            没有找到你想要的单位名称？<a onClick={() => onShowCreateUnitModal(index)}>点击添加</a>
                                                                        </span>
                                                                    </div>
                                                                )
                                                            )
                                                        }
                                                    >
                                                        {!!units
                                                            && units.map(unit => {
                                                                const invitingOrgIds = addInvitationForm.getFieldValue('invitationOrgIds') || [];
                                                                return (
                                                                    <Select.Option value={unit.id} key={unit.id} label={unit.name} disabled={!unit.orgType || invitingOrgIds.includes(unit.id)}>
                                                                        <div className="unitSelectBox" style={{ opacity: !unit.orgType ? 0.5 : 1, color: !unit.orgType ? '#888' : 'inherit', cursor: !unit.orgType ? 'not-allowed' : 'default' }}>
                                                                            <div className="unitName">{unit.name}</div>
                                                                            <div className="unitInfo">
                                                                                {!!prepareMedLevelGrade(unit) && <span style={{ marginRight: 10 }}>{prepareMedLevelGrade(unit)}</span>}
                                                                                <span style={{ marginRight: 10 }}>{orgTypeList.find(item => item.value === unit.orgType)?.label}</span>
                                                                                {!!prepareLocation(unit) && <span>{prepareLocation(unit)}</span>}
                                                                            </div>
                                                                            {!!prepareOrgAliase(unit) && (
                                                                                <div className="unitAliase">
                                                                                    <span>{prepareOrgAliase(unit)}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </Select.Option>
                                                                );
                                                            })}
                                                    </Select>
                                                </Form.Item>
                                                {fields.length > 1 ? (
                                                    <span className="anticon dynamic-delete-button" onClick={() => remove(field.name)}>
                                                        <i className="bi bi-dash-circle"></i>
                                                    </span>
                                                ) : null}
                                            </div>
                                        ))}
                                    </>
                                )}
                            </Form.List>
                        </Form.Item>
                    )}

                    {addExclusive && (
                        <Form.Item label="独家邀请机构" name="exclusiveInvitationOrgId" rules={[{ required: true, message: '请选择独家邀请机构' }]}>
                            <Select
                                optionLabelProp="label"
                                placeholder="请输入单位名称关键字检索"
                                suffixIcon={null}
                                allowClear
                                defaultActiveFirstOption={false}
                                onChange={onChangeUnit}
                                showSearch={{
                                    filterOption: false,
                                    onSearch: onChangeUnitSearchKeyword,
                                }}
                                styles={{ popup: { root: { width: '500px !important', minWidth: 500 } } }}
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                popupRender={menu => (
                                    <>
                                        {menu}
                                        {!!units && units.some(unit => !unit.orgType) && <div style={{ padding: '8px 12px', backgroundColor: '#fff7e6', color: '#fa8c16' }}>未分类的机构不可选择，请在数据中心-机构管理中完善后再选择</div>}
                                    </>
                                )}
                                notFoundContent={
                                    searchLoading ? (
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '5px 0' }}>
                                            <Spin />
                                        </div>
                                    ) : (
                                        unitSearchKeyword && (
                                            <div className="emptyUnitSelectBox">
                                                <span>
                                                    没有找到你想要的单位名称？<a onClick={() => onShowCreateUnitModal(0)}>点击添加</a>
                                                </span>
                                            </div>
                                        )
                                    )
                                }
                            >
                                {!!units
                                    && units.map(unit => (
                                        <Select.Option value={unit.id} key={unit.id} label={unit.name} disabled={!unit.orgType}>
                                            <div className="unitSelectBox" style={{ opacity: !unit.orgType ? 0.5 : 1, color: !unit.orgType ? '#888' : 'inherit', cursor: !unit.orgType ? 'not-allowed' : 'default' }}>
                                                <div className="unitName">{unit.name}</div>
                                                <div className="unitInfo">
                                                    {!!prepareMedLevelGrade(unit) && <span style={{ marginRight: 10 }}>{prepareMedLevelGrade(unit)}</span>}
                                                    <span style={{ marginRight: 10 }}>{orgTypeList.find(item => item.value === unit.orgType)?.label}</span>
                                                    {!!prepareLocation(unit) && <span>{prepareLocation(unit)}</span>}
                                                </div>
                                                {!!prepareOrgAliase(unit) && (
                                                    <div className="unitAliase">
                                                        <span>{prepareOrgAliase(unit)}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </Select.Option>
                                    ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            <Modal
                title="移除邀请方"
                width={600}
                open={visibleRemoveInvitation}
                onCancel={closeRemoveInvitationModal}
                centered
                footer={[
                    <Button key="reset" danger onClick={resetInvitationOrgs}>
                        重置邀请方
                    </Button>,
                    <Button key="ok" type="primary" onClick={confirmRemoveInvitation}>
                        确认移除
                    </Button>,
                ]}
            >
                <Form name="removeInvitationForm" form={removeInvitationForm} layout="vertical" initialValues={{ invitationOrgIds: [undefined] }}>
                    <Alert title={<>{!!selectedRowsGuests.current?.length && <>已选专家：{selectedRowsGuests.current.map(item => item.memUsername).join('、')}</>}</>} type="warning" style={{ marginBottom: 16 }} />

                    <Form.Item label="要移除的邀请方" name="invitationOrgIds" className="required">
                        <Form.List name="invitationOrgIds">
                            {(fields, { add, remove }) => (
                                <>
                                    <Button type="primary" size="small" ghost onClick={() => add()} disabled={fields.length >= 10} style={{ marginBottom: 10 }}>
                                        <i className="bi bi-plus-circle"></i>
                                        新增邀请方
                                    </Button>
                                    {fields.map((field, index) => (
                                        <div key={field.key} className="invitation-item" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                                            <Form.Item {...field} name={[field.name]} rules={[{ required: true, message: '请输入单位名称' }]} style={{ flex: 1, marginBottom: 0 }}>
                                                <Select
                                                    optionLabelProp="label"
                                                    placeholder="请输入单位名称关键字检索"
                                                    suffixIcon={null}
                                                    allowClear
                                                    defaultActiveFirstOption={false}
                                                    onChange={onChangeUnit}
                                                    showSearch={{
                                                        filterOption: false,
                                                        onSearch: onChangeUnitSearchKeyword,
                                                    }}
                                                    styles={{ popup: { root: { width: '500px !important', minWidth: 500 } } }}
                                                    getPopupContainer={triggerNode => triggerNode.parentNode}
                                                    popupRender={menu => (
                                                        <>
                                                            {menu}
                                                            {!!units && units.some(unit => !unit.orgType) && <div style={{ padding: '8px 12px', backgroundColor: '#fff7e6', color: '#fa8c16' }}>未分类的机构不可选择，请在数据中心-机构管理中完善后再选择</div>}
                                                        </>
                                                    )}
                                                    notFoundContent={
                                                        searchLoading ? (
                                                            <div style={{ display: 'flex', justifyContent: 'center', padding: '5px 0' }}>
                                                                <Spin />
                                                            </div>
                                                        ) : (
                                                            unitSearchKeyword && (
                                                                <div className="emptyUnitSelectBox">
                                                                    <span>
                                                                        没有找到你想要的单位名称？<a onClick={() => onShowCreateUnitModal(index)}>点击添加</a>
                                                                    </span>
                                                                </div>
                                                            )
                                                        )
                                                    }
                                                >
                                                    {!!units
                                                        && units.map(unit => (
                                                            <Select.Option value={unit.id} key={unit.id} label={unit.name} disabled={!unit.orgType}>
                                                                <div className="unitSelectBox" style={{ opacity: !unit.orgType ? 0.5 : 1, color: !unit.orgType ? '#888' : 'inherit', cursor: !unit.orgType ? 'not-allowed' : 'default' }}>
                                                                    <div className="unitName">{unit.name}</div>
                                                                    <div className="unitInfo">
                                                                        {!!prepareMedLevelGrade(unit) && <span style={{ marginRight: 10 }}>{prepareMedLevelGrade(unit)}</span>}
                                                                        <span style={{ marginRight: 10 }}>{orgTypeList.find(item => item.value === unit.orgType)?.label}</span>
                                                                        {!!prepareLocation(unit) && <span>{prepareLocation(unit)}</span>}
                                                                    </div>
                                                                    {!!prepareOrgAliase(unit) && (
                                                                        <div className="unitAliase">
                                                                            <span>{prepareOrgAliase(unit)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Select.Option>
                                                        ))}
                                                </Select>
                                            </Form.Item>
                                            {fields.length > 1 ? (
                                                <span className="anticon dynamic-delete-button" onClick={() => remove(field.name)}>
                                                    <i className="bi bi-dash-circle"></i>
                                                </span>
                                            ) : null}
                                        </div>
                                    ))}
                                </>
                            )}
                        </Form.List>
                    </Form.Item>
                </Form>
            </Modal>
            <Modal title="选择参会人员类别" open={identityModalOpen} onOk={handleIdentityOk} onCancel={handleIdentityCancel}>
                <Form name="identityForm" form={identityForm} autoComplete="off">
                    <Form.Item name="role" label="选择参会人员类别" rules={[{ required: true, message: '请选择参会人员类别' }]}>
                        <Select options={guestIdentityList} placeholder="请选择参会人员类别" />
                    </Form.Item>
                </Form>
            </Modal>

            <Modal title="查看行程单" open={imageModalOpen} footer={null} onCancel={() => setImageModalOpen(false)} className="journeyImageModal">
                <Image width={600} src={currImage} alt="嘉宾行程单" />
            </Modal>

            <Modal
                title="嘉宾会议任务"
                open={taskModalOpen}
                footer={null}
                width={1000}
                onCancel={() => {
                    setTaskModalOpen(false);
                    setTaskContent(null);
                }}
                className="taskCopyModal"
            >
                <div className="table-container">
                    <div className="memName">
                        <b>{taskContent?.memUsername}</b>的会议任务如下：
                    </div>
                    <table>
                        <thead>
                            <tr className="top-title">
                                <td className="date">时间</td>
                                <td className="place">地点</td>
                                <td className="topic">主题</td>
                                <td className="task">任务</td>
                            </tr>
                        </thead>
                        <tbody>
                            {taskContent?.agendasGuestsList
                                && !!taskContent?.agendasGuestsList?.length
                                && !!taskContent?.agendasGuestsList?.filter(g => g.invitationAccepted === 1)?.length
                                && taskContent.agendasGuestsList
                                    ?.filter(g => g.invitationAccepted === 1)
                                    .map((item, index) => (
                                        <tr key={index}>
                                            <td className="date">{convertDateTimeRangeToLocalText(item.startTime, item.endTime, 0)?.slice(5)}</td>
                                            <td className="place">{item.place}</td>
                                            <td className="topic" style={{ textAlign: 'left' }}>
                                                <span style={{ marginRight: 4 }}>{`${item.conferenceHallName} / ${item.title}`}</span>
                                            </td>
                                            <td className="task">{item.taskName}</td>
                                        </tr>
                                    ))}
                        </tbody>
                    </table>
                    {!!taskContent && (
                        <div className="copyBtn">
                            <Button onClick={() => copyAgendaTasks(taskContent)}>复制会议任务文本</Button>
                        </div>
                    )}
                </div>
            </Modal>

            <Modal title="显示嘉宾类型" open={visibleGuestType} onOk={confirmShowGuestType} onCancel={() => setVisibleGuestType(false)} centered>
                <Form name="basic" form={showGuestForm} layout="vertical" initialValues={{ guestsShowType: conference?.guestsShowType }}>
                    <Form.Item label="请选择显示嘉宾类型" name="guestsShowType" rules={[{ required: true, message: '请选择显示嘉宾类型' }]}>
                        <Radio.Group>
                            <Space>
                                <Radio value={1}>仅显示已确认出席嘉宾</Radio>
                                <Radio value={2}>显示已确认出席+拟邀嘉宾</Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="嘉宾同步报名签到"
                open={visibleSignUp}
                onCancel={() => setVisibleSignUp(false)}
                width={700}
                centered
                className="signup-modal"
                footer={[
                    <Button key="back" onClick={() => setVisibleSignUp(false)}>
                        取消
                    </Button>,
                    <Button key="submit" type="primary" loading={signUpLoading} onClick={confirmSignUp}>
                        确定
                    </Button>,
                ]}
            >
                <Alert title="提示：确认同步后不可撤销，若同步后调整了报名权益，可通过重新同步更新调整后权益" type="warning" />
                <Form>
                    <Form.Item label="已选嘉宾：">共计{selectedRowsGuests.current?.length}人</Form.Item>
                    <Form.Item label="同步报名身份：">
                        <div>VIP嘉宾</div>
                        <div>
                            <Text type="warning">
                                报名身份将体现在用户签到，后台签到列表等位置，若需调整请在<a style={{ cursor: 'auto' }}>报名设置</a>中调整。
                            </Text>
                        </div>
                    </Form.Item>
                    <Form.Item label="同步报名权益：">
                        <div>{benefits && !!benefits.length && benefits.map((item, index) => <span key={index} style={{ paddingRight: 10 }}>{`${item.name}*${item.amount}`}</span>)}</div>
                        <div>
                            <Text type="warning">
                                会务人员将按该权益发放，若需调整请在<a style={{ cursor: 'auto' }}>报名设置</a>中调整。
                            </Text>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="新增单位"
                className={'createUnitModal'}
                open={visibleCreateUnitModal}
                centered
                onCancel={onCancelCreateUnit}
                footer={[
                    <Button key="ok" type="primary" block size="large" onClick={onCreateUnit}>
                        确定添加
                    </Button>,
                ]}
            >
                <Form form={createUnitForm} size="large">
                    <Form.Item label="单位类型" name="orgType" rules={[{ required: true, message: '未选择单位类型' }]}>
                        <Select placeholder="请选择单位类型" options={orgTypeList.filter(item => !!item.value)} allowClear />
                    </Form.Item>
                    <Form.Item label="单位名称" name="unitName" rules={[{ required: true, message: '未填写所在单位名称' }]} extra="请与组织机构代码证/营业执照名称一致">
                        <Input placeholder="请输入您所在单位全称" maxLength={100} allowClear />
                    </Form.Item>
                </Form>
            </Modal>

            <AddGuest visibleAddGuestsDrawer={visibleAddGuestsDrawer} setVisibleAddGuestsDrawer={setVisibleAddGuestsDrawer} onResetGuest={onResetGuest} />

            <GuestTask visibleGuestTasksDrawer={visibleGuestTasksDrawer} closeGuestTasksDrawer={closeGuestTasksDrawer} rowItem={rowItem} getExpertList={getExpertList} />

            <GuestMessage visibleGuestDisplayInfoDrawer={visibleGuestDisplayInfoDrawer} closeGuestDisplayInfoDrawer={closeGuestDisplayInfoDrawer} rowItem={rowItem} />

            <GuestJourneyForm visibleGuestJourney={visibleGuestJourney} getGuestJourneyList={getExpertList} onClose={closeGuestJourney} conferenceId={conferenceId} expertId={currentGuestJourney?.id} currentGuestJourney={currentGuestJourney} setCurrentGuestJourney={setCurrentGuestJourney} />

            <ItineraryScheduleSetting visibleItineraryScheduleDrawer={visibleItineraryScheduleDrawer} setVisibleItineraryScheduleDrawer={setVisibleItineraryScheduleDrawer} getConferenceInfo={getConferenceInfo} conference={conference} />

            <Modal title="编辑嘉宾备注" open={remarksModalOpen} onCancel={closeRemarksEditor} onOk={handleSaveRemarks} confirmLoading={remarksSaving} okText="保存" cancelText="取消" destroyOnHidden centered>
                <Form form={remarksForm} layout="vertical" requiredMark={false}>
                    <Form.Item name="remarks" label="嘉宾备注">
                        <Input.TextArea maxLength={1000} showCount autoSize={{ minRows: 4, maxRows: 8 }} placeholder="请输入嘉宾备注" />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}
